/**
 * Rheina Related Products — carousel navigation
 */
class RheinaRelatedProducts extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {() => void} */
  #onScroll = () => this.#updateNavState();

  connectedCallback() {
    if (this.dataset.rheinaRpInit === 'true') {
      this.#bindTrack();
      this.#updateNavState();
      return;
    }

    this.dataset.rheinaRpInit = 'true';

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-rheina-rp-prev]')) {
        this.#scroll('prev');
      }

      if (event.target.closest('[data-rheina-rp-next]')) {
        this.#scroll('next');
      }
    });

    this.#resizeObserver = new ResizeObserver(() => this.#updateNavState());
    this.#resizeObserver.observe(this);

    this.#mutationObserver = new MutationObserver(() => {
      this.#bindTrack();
      this.#updateNavState();
    });
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    this.#bindTrack();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  #bindTrack() {
    const track = this.querySelector('[data-rheina-rp-track]');
    if (!(track instanceof HTMLElement) || track === this.#track) return;

    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track = track;
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  /**
   * @param {'prev' | 'next'} direction
   */
  #scroll(direction) {
    const track = this.querySelector('[data-rheina-rp-track]');
    if (!(track instanceof HTMLElement)) return;

    const card = track.querySelector('.rheina-rp__card');
    if (!card) return;

    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const distance = card.getBoundingClientRect().width + gap;

    track.scrollBy({
      left: direction === 'next' ? distance : -distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #updateNavState() {
    const track = this.querySelector('[data-rheina-rp-track]');
    const prev = this.querySelector('[data-rheina-rp-prev]');
    const next = this.querySelector('[data-rheina-rp-next]');

    if (!(track instanceof HTMLElement) || !(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 2;

    prev.disabled = !hasOverflow || scrollLeft <= 2;
    next.disabled = !hasOverflow || scrollLeft >= maxScroll - 2;

    const nav = this.querySelector('.rheina-rp__nav');
    if (nav instanceof HTMLElement) {
      nav.hidden = !hasOverflow;
    }
  }
}

if (!customElements.get('rheina-related-products')) {
  customElements.define('rheina-related-products', RheinaRelatedProducts);
}
